import { useEffect, useState, type FormEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { createLesson } from '../../../Redux/lessonsSlice';
import { selectActiveStudents } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import { useDisplayName } from '../../../Utils/useDisplayName';
import { ApiError } from '../../../Models/ApiModel';
import { localDateString } from '../../../Utils/LessonStatusUtil';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './QuickAddLesson.css';

/**
 * Adding a lesson, in as few decisions as possible.
 *
 * Picking a student fills the amount from their price, and leaving the time
 * empty lets the server inherit their recurring hour for that weekday. Both
 * defaults are there so the common case is a date and a tap.
 */
export function QuickAddLesson({ onClose, initialStudentName, initialDate, initialTime }: {
  onClose: () => void;
  initialStudentName?: string;
  initialDate?: string;
  initialTime?: string;
}) {
  const { tr } = useTranslation();
  const displayName = useDisplayName();
  const dispatch = useAppDispatch();
  const students = useAppSelector(selectActiveStudents);

  const initialStudent = students.find(candidate => candidate.name === initialStudentName) ?? students[0];
  const [student, setStudent] = useState(initialStudent?.name ?? '');
  const [date, setDate] = useState(initialDate ?? localDateString());
  const [time, setTime] = useState(initialTime ?? '');
  const [amount, setAmount] = useState(initialStudent?.price ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickStudent(name: string) {
    setStudent(name);
    const picked = students.find(candidate => candidate.name === name);
    if (picked) setAmount(picked.price);
  }

  useEffect(() => {
    if (initialStudentName) pickStudent(initialStudentName);
  }, [initialStudentName]);

  useEffect(() => {
    if (initialDate) setDate(initialDate);
    if (initialTime) setTime(initialTime);
  }, [initialDate, initialTime]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await dispatch(createLesson({
        student,
        date,
        amount,
        // Omitted rather than null, so the API inherits the recurring slot.
        ...(time ? { time } : {})
      })).unwrap();
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('addLesson')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="quick-add-lesson" className="primary-button" disabled={busy || !student}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
        </>
      }
    >
      <form id="quick-add-lesson" className="quick-add" onSubmit={submit}>
        <label className="field is-wide">
          <span>{tr('studentName')}</span>
          <select className="text-input" value={student} onChange={e => pickStudent(e.target.value)} required>
            {students.map(candidate => (
              <option key={candidate.name} value={candidate.name}>{displayName(candidate.name)}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{tr('date')}</span>
          <input className="text-input" type="date" value={date} onChange={e => setDate(e.target.value)} required />
        </label>
        <label className="field">
          <span>{tr('time')}</span>
          <input className="text-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </label>
        <label className="field is-wide">
          <span>{tr('amount')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/\D/g, ''))}
            required
          />
        </label>
        {error && <p className="quick-add-error" role="alert">{tr('errorGeneric')}</p>}
      </form>
    </Modal>
  );
}
